import React, { useState, useEffect, useCallback } from 'react'
import { supabase } from '../supabase'
import { useAuth } from '../context/AuthContext'
import { friendlyError } from '../lib/permissions'
import NoAccess from './NoAccess'

/*
  IMPORTANT — Run this SQL in Supabase SQL Editor before using
  the Attendance and Salary features:

  CREATE TABLE IF NOT EXISTS attendance (
    id         UUID PRIMARY KEY DEFAULT uuid_generate_v4(),
    staff_id   UUID NOT NULL REFERENCES staff(id) ON DELETE CASCADE,
    date       DATE NOT NULL,
    status     TEXT NOT NULL CHECK (status IN ('Present','Absent','Leave','Holiday')),
    notes      TEXT,
    created_at TIMESTAMPTZ NOT NULL DEFAULT NOW(),
    UNIQUE(staff_id, date)
  );
  ALTER TABLE attendance ENABLE ROW LEVEL SECURITY;
  CREATE POLICY "auth_all_attendance" ON attendance FOR ALL TO authenticated USING (true) WITH CHECK (true);

  CREATE TABLE IF NOT EXISTS salary_payments (
    id             UUID PRIMARY KEY DEFAULT uuid_generate_v4(),
    staff_id       UUID NOT NULL REFERENCES staff(id) ON DELETE CASCADE,
    month          INTEGER NOT NULL CHECK (month BETWEEN 1 AND 12),
    year           INTEGER NOT NULL,
    base_salary    NUMERIC(10,2) NOT NULL DEFAULT 0,
    deductions     NUMERIC(10,2) NOT NULL DEFAULT 0,
    bonus          NUMERIC(10,2) NOT NULL DEFAULT 0,
    net_payable    NUMERIC(10,2) NOT NULL DEFAULT 0,
    payment_status TEXT NOT NULL DEFAULT 'Pending' CHECK (payment_status IN ('Paid','Pending')),
    paid_at        TIMESTAMPTZ,
    notes          TEXT,
    created_at     TIMESTAMPTZ NOT NULL DEFAULT NOW(),
    UNIQUE(staff_id, month, year)
  );
  ALTER TABLE salary_payments ENABLE ROW LEVEL SECURITY;
  CREATE POLICY "auth_all_salary" ON salary_payments FOR ALL TO authenticated USING (true) WITH CHECK (true);
*/

// ─── Design tokens ─────────────────────────────────────────────────────────────
const T = {
  teal900: '#0d3d3d', teal800: '#0f5454', teal700: '#126868',
  teal600: '#148080', teal500: '#19a8a8', teal200: '#90e0e0',
  teal100: '#ccf2f2', teal50: '#edfafa',
  white: '#ffffff',
  gray50: '#f8fafc', gray100: '#f1f5f9', gray200: '#e2e8f0',
  gray300: '#cbd5e1', gray400: '#94a3b8', gray500: '#64748b',
  gray600: '#475569', gray700: '#334155', gray900: '#0f172a',
  red50: '#fff1f2', red100: '#fee2e2', red600: '#dc2626', red700: '#b91c1c',
  green50: '#f0fdf4', green600: '#16a34a', green700: '#15803d',
  amber50: '#fffbeb', amber100: '#fef3c7', amber600: '#d97706',
  blue50: '#eff6ff', blue600: '#2563eb',
  purple50: '#faf5ff', purple600: '#9333ea',
  font: "'Segoe UI', system-ui, -apple-system, sans-serif",
}

const ROLES = ['Dermatologist', 'Receptionist', 'Nurse/Assistant', 'Beautician/Therapist', 'Housekeeping']

// Non-admins never receive salary data — select these columns explicitly instead of '*'
const STAFF_PUBLIC_COLS = 'id, full_name, role, phone, email, joining_date, working_days, shift_timing, date_of_birth, clinic_id, created_at'
const DAYS  = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']


const ATT_STATUS = {
  Present:  { bg: T.green50,  color: T.green700, border: '#bbf7d0', dot: T.green600 },
  Absent:   { bg: T.red50,    color: T.red700,   border: '#fecdd3', dot: T.red600  },
  Leave:    { bg: T.amber50,  color: T.amber600,  border: '#fde68a', dot: T.amber600 },
  Holiday:  { bg: T.blue50,   color: T.blue600,  border: '#bfdbfe', dot: T.blue600 },
}

const ROLE_COLORS = {
  'Dermatologist':       ['#0f5454','#2ecece'],
  'Receptionist':        ['#1e3a5f','#60a5fa'],
  'Nurse/Assistant':     ['#3b1f6e','#a78bfa'],
  'Beautician/Therapist':['#4a1942','#e879f9'],
  'Housekeeping':        ['#1a4731','#34d399'],
}

function initials(name = '') { return name.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2) || '?' }
function avatarColor(role)   { return ROLE_COLORS[role] || ['#082828', '#19a8a8'] }
// Local calendar date (toISOString is UTC and gives yesterday before 05:30 IST)
function isoDate(d = new Date()) { return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}` }
function fmtDate(d) { return d ? new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : '—' }
function fmtCur(n)  { return n != null ? '₹' + Number(n).toLocaleString('en-IN') : '—' }

const MONTH_NAMES = ['January','February','March','April','May','June','July','August','September','October','November','December']

// ─── Shared form primitives ────────────────────────────────────────────────────
const sx = {
  width: '100%', padding: '9px 12px', border: `1.5px solid ${T.gray200}`,
  borderRadius: 8, fontSize: 13.5, color: T.gray900, background: T.gray50,
  outline: 'none', fontFamily: T.font, boxSizing: 'border-box',
}
const focusSx = (e, on) => { e.target.style.borderColor = on ? T.teal500 : T.gray200; e.target.style.boxShadow = on ? `0 0 0 3px ${T.teal100}` : 'none' }
function FInput({ value, onChange, placeholder, type = 'text', ...rest }) {
  return <input type={type} value={value} onChange={onChange} placeholder={placeholder} style={sx} onFocus={e => focusSx(e, true)} onBlur={e => focusSx(e, false)} {...rest} />
}
function FSelect({ value, onChange, children }) {
  return <select value={value} onChange={onChange} style={{ ...sx, cursor: 'pointer' }} onFocus={e => focusSx(e, true)} onBlur={e => focusSx(e, false)}>{children}</select>
}
function FLabel({ children }) { return <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: T.gray600, marginBottom: 5 }}>{children}</label> }
function FF({ label, children, half, third }) {
  const w = third ? 'calc(33.33% - 9px)' : half ? 'calc(50% - 6px)' : '100%'
  return <div style={{ marginBottom: 14, width: w }}><FLabel>{label}</FLabel>{children}</div>
}
function FRow({ children }) { return <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>{children}</div> }

function ErrBox({ msg }) {
  return msg ? <div style={{ background: T.red50, border: '1px solid #fecdd3', borderRadius: 8, padding: '10px 14px', color: T.red600, fontSize: 13, marginBottom: 16 }}>⚠️ {msg}</div> : null
}
function Btn({ children, onClick, disabled, variant = 'primary', small }) {
  const v = {
    primary:   { bg: T.teal600,  color: T.white,   border: 'none' },
    secondary: { bg: T.white,    color: T.gray600,  border: `1.5px solid ${T.gray200}` },
    danger:    { bg: T.red600,   color: T.white,    border: 'none' },
    green:     { bg: T.green600, color: T.white,    border: 'none' },
    amber:     { bg: T.amber600, color: T.white,    border: 'none' },
  }[variant]
  return (
    <button onClick={onClick} disabled={disabled} style={{
      padding: small ? '6px 14px' : '9px 20px', borderRadius: 8, border: v.border,
      background: disabled ? T.gray200 : v.bg, color: disabled ? T.gray400 : v.color,
      fontSize: small ? 12 : 13, fontWeight: 700, cursor: disabled ? 'not-allowed' : 'pointer',
      fontFamily: T.font, whiteSpace: 'nowrap',
    }}>{children}</button>
  )
}

// ═══════════════════════════════════════════════════════════════════════════════
// ADD / EDIT STAFF MODAL
// ═══════════════════════════════════════════════════════════════════════════════
function StaffModal({ member, onClose, onSaved }) {
  const { userProfile } = useAuth()
  const blank = { full_name: '', role: '', phone: '', email: '', joining_date: '', salary: '', working_days: [], shift_timing: '' }
  const [form, setForm] = useState(member ? {
    ...blank, ...member,
    salary: member.salary ?? '',
    working_days: member.working_days || [],
    joining_date: member.joining_date || '',
  } : blank)
  const [saving, setSaving] = useState(false)
  const [error, setError]   = useState('')
  function set(k, v) { setForm(f => ({ ...f, [k]: v })) }
  function toggleDay(d) {
    setForm(f => ({
      ...f,
      working_days: f.working_days.includes(d) ? f.working_days.filter(x => x !== d) : [...f.working_days, d],
    }))
  }
  async function handleSave() {
    if (!form.full_name.trim()) { setError('Full name is required.'); return }
    if (!form.role)             { setError('Please select a role.'); return }
    if (!form.phone.trim())     { setError('Phone number is required.'); return }
    setError(''); setSaving(true)
    const payload = {
      full_name:    form.full_name.trim(),
      role:         form.role,
      phone:        form.phone.trim(),
      email:        form.email.trim() || null,
      joining_date: form.joining_date || null,
      salary:       form.salary !== '' ? Number(form.salary) : null,
      working_days: form.working_days.length ? form.working_days : null,
      shift_timing: form.shift_timing.trim() || null,
      clinic_id:    userProfile?.clinic_id,
    }
    try {
      let result
      if (member?.id) {
        const { data, error } = await supabase.from('staff').update(payload).eq('id', member.id).select().single()
        if (error) throw error; result = data
      } else {
        const { data, error } = await supabase.from('staff').insert(payload).select().single()
        if (error) throw error; result = data
      }
      onSaved(result)
    } catch (e) { setError(friendlyError(e)); setSaving(false) }
  }

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(8,40,40,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 200, padding: 20, fontFamily: T.font }} onClick={e => { if (e.target === e.currentTarget) onClose() }}>
      <div style={{ background: T.white, borderRadius: 16, width: '100%', maxWidth: 560, maxHeight: '92vh', display: 'flex', flexDirection: 'column', boxShadow: '0 24px 64px rgba(0,0,0,0.35)', overflow: 'hidden' }}>
        {/* Header */}
        <div style={{ padding: '18px 24px', background: T.teal900, borderRadius: '16px 16px 0 0', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
          <div>
            <h2 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: T.white }}>{member ? 'Edit Staff Member' : 'Add Staff Member'}</h2>
            <p style={{ margin: '2px 0 0', fontSize: 12, color: T.teal200 }}>{member ? `Editing ${member.full_name}` : 'Register a new team member'}</p>
          </div>
          <button onClick={onClose} style={{ background: 'rgba(255,255,255,0.12)', border: 'none', borderRadius: 8, color: T.white, width: 32, height: 32, fontSize: 18, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>×</button>
        </div>
        {/* Body */}
        <div style={{ overflowY: 'auto', padding: '22px 24px', flex: 1 }}>
          <ErrBox msg={error} />
          <FF label="Full Name *"><FInput value={form.full_name} onChange={e => set('full_name', e.target.value)} placeholder="e.g. Dr. Priya Shah" /></FF>
          <FRow>
            <FF label="Role *" half>
              <FSelect value={form.role} onChange={e => set('role', e.target.value)}>
                <option value="">Select role…</option>
                {ROLES.map(r => <option key={r} value={r}>{r}</option>)}
              </FSelect>
            </FF>
            <FF label="Phone *" half><FInput value={form.phone} onChange={e => set('phone', e.target.value)} placeholder="9876543210" /></FF>
          </FRow>
          <FRow>
            <FF label="Email" half><FInput type="email" value={form.email} onChange={e => set('email', e.target.value)} placeholder="email@clinic.com" /></FF>
            <FF label="Date of Joining" half><FInput type="date" value={form.joining_date} onChange={e => set('joining_date', e.target.value)} /></FF>
          </FRow>
          <FRow>
            <FF label="Salary (₹ / month)" half><FInput type="number" min="0" value={form.salary} onChange={e => set('salary', e.target.value)} placeholder="e.g. 25000" /></FF>
            <FF label="Shift Timing" half><FInput value={form.shift_timing} onChange={e => set('shift_timing', e.target.value)} placeholder="e.g. 09:00 - 18:00" /></FF>
          </FRow>
          {/* Working days checkboxes */}
          <FF label="Working Days">
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 2 }}>
              {DAYS.map(d => {
                const active = form.working_days.includes(d)
                return (
                  <button key={d} type="button" onClick={() => toggleDay(d)} style={{
                    padding: '6px 12px', borderRadius: 8, cursor: 'pointer', fontFamily: T.font,
                    border: `1.5px solid ${active ? T.teal500 : T.gray200}`,
                    background: active ? T.teal600 : T.white,
                    color: active ? T.white : T.gray500, fontSize: 13, fontWeight: 600,
                    transition: 'all 0.15s',
                  }}>{d}</button>
                )
              })}
            </div>
          </FF>
        </div>
        {/* Footer */}
        <div style={{ padding: '14px 24px', borderTop: `1px solid ${T.gray100}`, display: 'flex', justifyContent: 'flex-end', gap: 10, flexShrink: 0 }}>
          <Btn variant="secondary" onClick={onClose}>Cancel</Btn>
          <Btn onClick={handleSave} disabled={saving}>{saving ? 'Saving…' : member ? 'Save Changes' : 'Add Member'}</Btn>
        </div>
      </div>
    </div>
  )
}

// ═══════════════════════════════════════════════════════════════════════════════
// ATTENDANCE CALENDAR
// ═══════════════════════════════════════════════════════════════════════════════
function AttendanceTab({ staffId }) {
  const { userProfile, can } = useAuth()
  const canMark = can('attendance.mark')
  const now = new Date()
  const [year, setYear]   = useState(now.getFullYear())
  const [month, setMonth] = useState(now.getMonth()) // 0-indexed
  const [records, setRecords]   = useState({})   // date string → status
  const [, setLoading]          = useState(true)
  const [marking, setMarking]   = useState(false)
  const [markStatus, setMarkStatus] = useState('Present')
  const [tableError, setTableError] = useState(false)

  const loadRecords = useCallback(async () => {
    setLoading(true)
    const start = `${year}-${String(month + 1).padStart(2, '0')}-01`
    const lastDay = new Date(year, month + 1, 0).getDate()
    const end   = `${year}-${String(month + 1).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`
    const { data, error } = await supabase
      .from('attendance').select('date,status').eq('staff_id', staffId).gte('date', start).lte('date', end)
    if (error) { setTableError(true); setLoading(false); return }
    const map = {}
    ;(data || []).forEach(r => { map[r.date] = r.status })
    setRecords(map)
    setLoading(false)
  }, [staffId, year, month])

  useEffect(() => { loadRecords() }, [loadRecords])

  async function markToday() {
    setMarking(true)
    const today = isoDate()
    const { error } = await supabase.from('attendance').upsert({ staff_id: staffId, date: today, status: markStatus, clinic_id: userProfile?.clinic_id }, { onConflict: 'staff_id,date' })
    if (!error) setRecords(r => ({ ...r, [today]: markStatus }))
    else alert(friendlyError(error))
    setMarking(false)
  }

  // Build calendar grid
  const firstDay = new Date(year, month, 1).getDay() // 0=Sun
  const startOffset = firstDay === 0 ? 6 : firstDay - 1 // Mon-first
  const daysInMonth = new Date(year, month + 1, 0).getDate()
  const cells = Array(startOffset).fill(null).concat(Array.from({ length: daysInMonth }, (_, i) => i + 1))

  // Monthly summary
  const summary = { Present: 0, Absent: 0, Leave: 0, Holiday: 0 }
  Object.values(records).forEach(s => { if (summary[s] !== undefined) summary[s]++ })

  const todayStr = isoDate()
  const todayStatus = records[todayStr]

  if (tableError) return (
    <div style={{ padding: 20, background: T.amber50, borderRadius: 10, border: `1px solid #fde68a`, fontSize: 13, color: T.amber600 }}>
      ⚠️ Attendance table not found. Please run the SQL at the top of <code>Staff.js</code> in Supabase SQL Editor.
    </div>
  )

  return (
    <div>
      {/* Month navigation */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
        <button onClick={() => { let m = month - 1, y = year; if (m < 0) { m = 11; y-- } setMonth(m); setYear(y) }}
          style={{ ...navBtnSm }}>‹</button>
        <span style={{ fontSize: 14, fontWeight: 700, color: T.gray900 }}>{MONTH_NAMES[month]} {year}</span>
        <button onClick={() => { let m = month + 1, y = year; if (m > 11) { m = 0; y++ } setMonth(m); setYear(y) }}
          style={{ ...navBtnSm }}>›</button>
      </div>

      {/* Calendar */}
      <div style={{ background: T.gray50, borderRadius: 10, overflow: 'hidden', border: `1px solid ${T.gray200}`, marginBottom: 14 }}>
        {/* Day headers */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7,1fr)', background: T.teal900 }}>
          {['M','T','W','T','F','S','S'].map((d, i) => (
            <div key={i} style={{ textAlign: 'center', padding: '6px 0', fontSize: 11, fontWeight: 700, color: T.teal200 }}>{d}</div>
          ))}
        </div>
        {/* Day cells */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7,1fr)' }}>
          {cells.map((day, i) => {
            if (!day) return <div key={i} />
            const ds = `${year}-${String(month + 1).padStart(2,'0')}-${String(day).padStart(2,'0')}`
            const status = records[ds]
            const sc = status ? ATT_STATUS[status] : null
            const isT = ds === todayStr
            return (
              <div key={i} style={{
                textAlign: 'center', padding: '6px 0',
                borderTop: `1px solid ${T.gray200}`,
                background: isT ? T.teal50 : T.white,
              }}>
                <div style={{
                  width: 28, height: 28, borderRadius: '50%', margin: '0 auto',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 12, fontWeight: isT ? 800 : 500,
                  background: sc ? sc.bg : isT ? T.teal100 : 'transparent',
                  color: sc ? sc.color : isT ? T.teal700 : T.gray700,
                  border: isT && !sc ? `2px solid ${T.teal400}` : 'none',
                  title: status || '',
                }}>
                  {day}
                </div>
                {status && (
                  <div style={{ fontSize: 8, fontWeight: 700, color: sc.color, marginTop: 1, letterSpacing: '0.2px' }}>
                    {status === 'Present' ? 'P' : status === 'Absent' ? 'A' : status === 'Leave' ? 'L' : 'H'}
                  </div>
                )}
              </div>
            )
          })}
        </div>
      </div>

      {/* Legend */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 14, flexWrap: 'wrap' }}>
        {Object.entries(ATT_STATUS).map(([s, c]) => (
          <div key={s} style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 11.5 }}>
            <span style={{ width: 10, height: 10, borderRadius: '50%', background: c.dot }} />
            <span style={{ color: T.gray600 }}>{s}: <strong style={{ color: c.color }}>{summary[s]}</strong></span>
          </div>
        ))}
      </div>

      {/* Mark today */}
      {canMark && <div style={{ background: T.gray50, borderRadius: 10, padding: '14px', border: `1px solid ${T.gray200}` }}>
        <div style={{ fontSize: 12.5, fontWeight: 700, color: T.gray700, marginBottom: 10 }}>
          Mark Attendance — Today ({new Date().toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })})
        </div>
        {todayStatus ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ background: ATT_STATUS[todayStatus]?.bg, color: ATT_STATUS[todayStatus]?.color, border: `1px solid ${ATT_STATUS[todayStatus]?.border}`, borderRadius: 20, padding: '4px 14px', fontSize: 12.5, fontWeight: 700 }}>
              ● {todayStatus}
            </span>
            <button onClick={() => setRecords(r => { const n = { ...r }; delete n[todayStr]; return n })}
              style={{ fontSize: 12, color: T.gray400, background: 'none', border: 'none', cursor: 'pointer', fontFamily: T.font, textDecoration: 'underline' }}>
              Change
            </button>
          </div>
        ) : (
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
            {Object.keys(ATT_STATUS).map(s => (
              <button key={s} onClick={() => setMarkStatus(s)} style={{
                padding: '6px 12px', borderRadius: 8, cursor: 'pointer', fontFamily: T.font,
                border: `1.5px solid ${markStatus === s ? ATT_STATUS[s].dot : T.gray200}`,
                background: markStatus === s ? ATT_STATUS[s].bg : T.white,
                color: markStatus === s ? ATT_STATUS[s].color : T.gray500,
                fontSize: 12.5, fontWeight: 600, transition: 'all 0.12s',
              }}>{s}</button>
            ))}
            <Btn onClick={markToday} disabled={marking} small>{marking ? '…' : 'Save'}</Btn>
          </div>
        )}
      </div>}
    </div>
  )
}

// ═══════════════════════════════════════════════════════════════════════════════
// SALARY TAB
// ═══════════════════════════════════════════════════════════════════════════════
function SalaryTab({ member }) {
  const { userProfile } = useAuth()
  const now = new Date()
  const [year, setYear]   = useState(now.getFullYear())
  const [month, setMonth] = useState(now.getMonth() + 1) // 1-indexed
  const [record, setRecord]   = useState(null)
  const [history, setHistory] = useState([])
  const [loading, setLoading] = useState(true)
  const [tableError, setTableError] = useState(false)
  const [editing, setEditing] = useState(false)
  const [form, setForm]   = useState({ base_salary: member.salary || 0, deductions: 0, bonus: 0, notes: '' })
  const [saving, setSaving] = useState(false)

  function setF(k, v) { setForm(f => ({ ...f, [k]: v })) }
  const net = Number(form.base_salary) - Number(form.deductions) + Number(form.bonus)

  const loadSalary = useCallback(async () => {
    setLoading(true)
    const { data: rec, error } = await supabase
      .from('salary_payments').select('*').eq('staff_id', member.id).eq('month', month).eq('year', year).maybeSingle()
    if (error) { setTableError(true); setLoading(false); return }
    setRecord(rec)
    if (rec) setForm({ base_salary: rec.base_salary, deductions: rec.deductions, bonus: rec.bonus, notes: rec.notes || '' })

    const { data: hist } = await supabase
      .from('salary_payments').select('*').eq('staff_id', member.id).order('year', { ascending: false }).order('month', { ascending: false }).limit(12)
    setHistory(hist || [])
    setLoading(false)
  }, [member.id, month, year])

  useEffect(() => { if (userProfile?.role === 'admin') loadSalary() }, [loadSalary, userProfile?.role])

  if (userProfile?.role !== 'admin') return <NoAccess compact detail="Salary details are visible to admins only." />

  async function handleSave() {
    if (userProfile?.role !== 'admin') { alert('Only admins can manage payroll'); return }
    setSaving(true)
    const payload = {
      staff_id:  member.id, month, year,
      clinic_id: userProfile?.clinic_id,
      base_salary: Number(form.base_salary),
      deductions:  Number(form.deductions),
      bonus:       Number(form.bonus),
      net_payable: net,
      payment_status: record?.payment_status || 'Pending',
      notes: form.notes || null,
    }
    const { data, error } = await supabase.from('salary_payments').upsert(payload, { onConflict: 'staff_id,month,year' }).select().single()
    if (!error) { setRecord(data); setEditing(false); loadSalary() }
    else alert(friendlyError(error))
    setSaving(false)
  }

  async function markPaid() {
    if (!record) return
    if (userProfile?.role !== 'admin') { alert('Only admins can manage payroll'); return }
    const { data } = await supabase.from('salary_payments')
      .update({ payment_status: 'Paid', paid_at: new Date().toISOString() })
      .eq('id', record.id).select().single()
    if (data) setRecord(data)
  }

  if (tableError) return (
    <div style={{ padding: 20, background: T.amber50, borderRadius: 10, border: `1px solid #fde68a`, fontSize: 13, color: T.amber600 }}>
      ⚠️ Salary table not found. Run the SQL at the top of <code>Staff.js</code> in Supabase SQL Editor.
    </div>
  )

  return (
    <div>
      {/* Month nav */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
        <button onClick={() => { let m = month - 1, y = year; if (m < 1) { m = 12; y-- } setMonth(m); setYear(y) }} style={navBtnSm}>‹</button>
        <span style={{ fontSize: 14, fontWeight: 700, color: T.gray900 }}>{MONTH_NAMES[month - 1]} {year}</span>
        <button onClick={() => { let m = month + 1, y = year; if (m > 12) { m = 1; y++ } setMonth(m); setYear(y) }} style={navBtnSm}>›</button>
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: 24, color: T.gray400, fontSize: 13 }}>Loading…</div>
      ) : editing || !record ? (
        /* Salary form */
        <div style={{ background: T.gray50, borderRadius: 10, padding: 16, border: `1px solid ${T.gray200}` }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: T.gray700, marginBottom: 12 }}>
            {record ? 'Edit Salary Record' : 'Create Salary Record'} — {MONTH_NAMES[month - 1]} {year}
          </div>
          {[['Base Salary (₹)', 'base_salary'], ['Deductions (₹)', 'deductions'], ['Bonus (₹)', 'bonus']].map(([label, key]) => (
            <div key={key} style={{ marginBottom: 10 }}>
              <FLabel>{label}</FLabel>
              <input type="number" min="0" step="0.01" value={form[key]} onChange={e => setF(key, e.target.value)}
                style={{ ...sx, width: '100%' }}
                onFocus={e => focusSx(e, true)} onBlur={e => focusSx(e, false)} />
            </div>
          ))}
          <div style={{ background: T.teal50, borderRadius: 8, padding: '10px 14px', marginBottom: 12, display: 'flex', justifyContent: 'space-between' }}>
            <span style={{ fontSize: 13, fontWeight: 600, color: T.teal700 }}>Net Payable</span>
            <span style={{ fontSize: 18, fontWeight: 800, color: T.teal600 }}>{fmtCur(net)}</span>
          </div>
          <div style={{ marginBottom: 12 }}>
            <FLabel>Notes</FLabel>
            <input value={form.notes} onChange={e => setF('notes', e.target.value)} placeholder="Optional notes…"
              style={sx} onFocus={e => focusSx(e, true)} onBlur={e => focusSx(e, false)} />
          </div>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            {record && <Btn variant="secondary" small onClick={() => setEditing(false)}>Cancel</Btn>}
            <Btn small onClick={handleSave} disabled={saving}>{saving ? 'Saving…' : 'Save Record'}</Btn>
          </div>
        </div>
      ) : (
        /* Salary summary card */
        <div style={{ background: T.gray50, borderRadius: 10, border: `1px solid ${T.gray200}`, overflow: 'hidden', marginBottom: 14 }}>
          <div style={{ background: record.payment_status === 'Paid' ? T.green600 : T.amber600, padding: '10px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontSize: 13, fontWeight: 700, color: T.white }}>{MONTH_NAMES[month - 1]} {year} Salary</span>
            <span style={{ background: 'rgba(255,255,255,0.25)', color: T.white, borderRadius: 20, padding: '2px 12px', fontSize: 12, fontWeight: 700 }}>
              {record.payment_status}
            </span>
          </div>
          <div style={{ padding: 16 }}>
            {[['Base Salary', fmtCur(record.base_salary)], ['Deductions', '– ' + fmtCur(record.deductions)], ['Bonus', '+ ' + fmtCur(record.bonus)]].map(([l, v]) => (
              <div key={l} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: `1px solid ${T.gray100}`, fontSize: 13 }}>
                <span style={{ color: T.gray600 }}>{l}</span>
                <span style={{ color: T.gray900, fontWeight: 600 }}>{v}</span>
              </div>
            ))}
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0 4px', fontSize: 15, fontWeight: 800 }}>
              <span style={{ color: T.gray700 }}>Net Payable</span>
              <span style={{ color: T.teal600 }}>{fmtCur(record.net_payable)}</span>
            </div>
            {record.paid_at && <div style={{ fontSize: 11.5, color: T.gray400, marginTop: 4 }}>Paid on {fmtDate(record.paid_at)}</div>}
            {record.notes && <div style={{ fontSize: 12, color: T.gray500, marginTop: 8, fontStyle: 'italic' }}>{record.notes}</div>}
          </div>
          <div style={{ padding: '10px 16px', borderTop: `1px solid ${T.gray100}`, display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <Btn variant="secondary" small onClick={() => setEditing(true)}>✏️ Edit</Btn>
            {record.payment_status === 'Pending' && <Btn variant="green" small onClick={markPaid}>✅ Mark Paid</Btn>}
          </div>
        </div>
      )}

      {!record && !editing && (
        <div style={{ marginTop: 8 }}>
          <Btn small onClick={() => setEditing(true)}>+ Create Salary Record</Btn>
        </div>
      )}

      {/* Payment history */}
      {history.length > 1 && (
        <div style={{ marginTop: 16 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: T.gray500, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 8 }}>Payment History</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {history.filter(h => !(h.month === month && h.year === year)).slice(0, 6).map(h => (
              <div key={h.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: T.white, borderRadius: 8, padding: '8px 12px', border: `1px solid ${T.gray100}` }}>
                <span style={{ fontSize: 13, color: T.gray700, fontWeight: 600 }}>{MONTH_NAMES[h.month - 1]} {h.year}</span>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <span style={{ fontSize: 13, fontWeight: 700, color: T.gray900 }}>{fmtCur(h.net_payable)}</span>
                  <span style={{
                    background: h.payment_status === 'Paid' ? T.green50 : T.amber50,
                    color: h.payment_status === 'Paid' ? T.green700 : T.amber600,
                    border: `1px solid ${h.payment_status === 'Paid' ? '#bbf7d0' : '#fde68a'}`,
                    borderRadius: 20, padding: '2px 9px', fontSize: 11, fontWeight: 700,
                  }}>{h.payment_status}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

// ═══════════════════════════════════════════════════════════════════════════════
// STAFF PROFILE PANEL
// ═══════════════════════════════════════════════════════════════════════════════
function ProfilePanel({ member, onClose, onEdit, onDeleted, todayAtt }) {
  const { userProfile } = useAuth()
  const isAdmin = userProfile?.role === 'admin'
  const [tab, setTab]         = useState('info')
  const [confirmDel, setConf] = useState(false)
  const [deleting, setDel]    = useState(false)
  const [bg, fg] = avatarColor(member.role)

  async function handleDelete() {
    if (userProfile?.role !== 'admin') { alert('Only admins can delete staff'); return }
    setDel(true)
    const { error } = await supabase.from('staff').delete().eq('id', member.id)
    if (!error) onDeleted(member.id)
    else { setDel(false); alert(friendlyError(error, 'Delete failed')) }
  }

  const attSc = todayAtt ? ATT_STATUS[todayAtt] : null

  const tabs = [
    { id: 'info',       label: '👤 Info'       },
    { id: 'attendance', label: '📅 Attendance'  },
    ...(isAdmin ? [{ id: 'salary', label: '💰 Salary' }] : []),
  ]

  return (
    <div style={{ width: 400, flexShrink: 0, background: T.white, borderLeft: `1px solid ${T.gray200}`, display: 'flex', flexDirection: 'column', height: '100%', fontFamily: T.font, overflowY: 'auto', animation: 'slideIn 0.22s ease' }}>
      <style>{`@keyframes slideIn { from { transform: translateX(40px); opacity: 0 } to { transform: translateX(0); opacity: 1 } }`}</style>

      {/* Sticky close bar */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px', borderBottom: `1px solid ${T.gray100}`, position: 'sticky', top: 0, background: T.white, zIndex: 10 }}>
        <span style={{ fontSize: 13, fontWeight: 700, color: T.gray700 }}>Staff Profile</span>
        <div style={{ display: 'flex', gap: 8 }}>
          {isAdmin && <button onClick={() => onEdit(member)} style={{ padding: '5px 12px', borderRadius: 7, border: `1.5px solid ${T.teal500}`, background: T.teal50, color: T.teal700, fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: T.font }}>✏️ Edit</button>}
          <button onClick={onClose} style={{ background: T.gray100, border: 'none', borderRadius: 7, width: 30, height: 30, cursor: 'pointer', fontSize: 16, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>×</button>
        </div>
      </div>

      {/* Avatar banner */}
      <div style={{ padding: '20px', background: `linear-gradient(135deg, ${bg}, ${bg}cc)`, display: 'flex', alignItems: 'center', gap: 14 }}>
        <div style={{ width: 60, height: 60, borderRadius: '50%', background: `linear-gradient(135deg, ${fg}44, ${fg}88)`, border: `3px solid ${fg}`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20, fontWeight: 800, color: T.white, flexShrink: 0 }}>
          {initials(member.full_name)}
        </div>
        <div>
          <div style={{ fontSize: 17, fontWeight: 800, color: T.white }}>{member.full_name}</div>
          <div style={{ fontSize: 12, color: fg + 'cc', marginTop: 3 }}>{member.role}</div>
          {attSc && (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, marginTop: 5, background: attSc.bg, color: attSc.color, border: `1px solid ${attSc.border}`, borderRadius: 20, padding: '2px 10px', fontSize: 11.5, fontWeight: 700 }}>
              <span style={{ width: 6, height: 6, borderRadius: '50%', background: attSc.dot }} />
              {todayAtt} Today
            </span>
          )}
        </div>
      </div>

      {/* Tab bar */}
      <div style={{ display: 'flex', borderBottom: `2px solid ${T.gray100}` }}>
        {tabs.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)} style={{
            flex: 1, padding: '10px 6px', background: 'none', border: 'none', cursor: 'pointer',
            fontFamily: T.font, fontSize: 12, fontWeight: tab === t.id ? 700 : 500,
            color: tab === t.id ? T.teal600 : T.gray400,
            borderBottom: tab === t.id ? `2px solid ${T.teal500}` : '2px solid transparent',
            marginBottom: -2, whiteSpace: 'nowrap',
          }}>{t.label}</button>
        ))}
      </div>

      {/* Tab content */}
      <div style={{ padding: 16, flex: 1 }}>
        {tab === 'info' && (
          <div>
            {[
              ['Phone',         member.phone],
              ['Email',         member.email],
              ['Joined',        fmtDate(member.joining_date)],
              isAdmin && member.salary != null ? ['Salary', fmtCur(member.salary) + ' / month'] : null,
              ['Shift',         member.shift_timing],
              ['Working Days',  (member.working_days || []).join(', ')],
            ].filter(Boolean).map(([l, v]) => v ? (
              <div key={l} style={{ display: 'flex', gap: 10, padding: '8px 0', borderBottom: `1px solid ${T.gray100}` }}>
                <span style={{ fontSize: 12, fontWeight: 600, color: T.gray400, width: 100, flexShrink: 0 }}>{l}</span>
                <span style={{ fontSize: 13, color: T.gray700 }}>{v}</span>
              </div>
            ) : null)}
          </div>
        )}
        {tab === 'attendance' && <AttendanceTab staffId={member.id} />}
        {tab === 'salary' && isAdmin && <SalaryTab member={member} />}
      </div>

      {/* Delete — admin only */}
      {isAdmin && <div style={{ padding: '0 16px 16px' }}>
        {!confirmDel ? (
          <button onClick={() => setConf(true)} style={{ width: '100%', padding: 8, borderRadius: 8, border: '1.5px solid #fecdd3', background: T.red50, color: T.red600, fontSize: 12.5, fontWeight: 600, cursor: 'pointer', fontFamily: T.font }}>
            🗑 Remove Staff Member
          </button>
        ) : (
          <div style={{ background: T.red50, border: '1px solid #fecdd3', borderRadius: 10, padding: 14, textAlign: 'center' }}>
            <p style={{ margin: '0 0 10px', fontSize: 13, color: T.red600, fontWeight: 600 }}>Permanently remove this staff member?</p>
            <div style={{ display: 'flex', gap: 8 }}>
              <Btn variant="secondary" small onClick={() => setConf(false)}>Cancel</Btn>
              <Btn variant="danger" small onClick={handleDelete} disabled={deleting}>{deleting ? 'Removing…' : 'Yes, Remove'}</Btn>
            </div>
          </div>
        )}
      </div>}
    </div>
  )
}

// ─── Staff card ────────────────────────────────────────────────────────────────
function StaffCard({ member, todayAtt, onSelect, onMarkAtt, isSelected, canMark }) {
  const [bg, fg] = avatarColor(member.role)
  const attSc = todayAtt ? ATT_STATUS[todayAtt] : null
  const [hover, setHover] = useState(false)

  return (
    <div
      onClick={() => onSelect(member)}
      onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)}
      style={{
        background: T.white, borderRadius: 14, overflow: 'hidden', cursor: 'pointer',
        border: `2px solid ${isSelected ? T.teal500 : hover ? T.teal200 : T.gray200}`,
        boxShadow: isSelected ? `0 0 0 3px ${T.teal100}` : hover ? '0 4px 16px rgba(0,0,0,0.08)' : '0 1px 4px rgba(0,0,0,0.05)',
        transition: 'all 0.15s', display: 'flex', flexDirection: 'column',
      }}
    >
      {/* Colour stripe */}
      <div style={{ height: 6, background: `linear-gradient(90deg, ${bg}, ${fg})` }} />
      <div style={{ padding: '16px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
          <div style={{ width: 46, height: 46, borderRadius: '50%', background: `linear-gradient(135deg, ${bg}, ${fg})`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, fontWeight: 800, color: T.white, flexShrink: 0 }}>
            {initials(member.full_name)}
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: T.gray900, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{member.full_name}</div>
            <div style={{ fontSize: 11.5, color: T.gray500, marginTop: 1 }}>{member.role}</div>
          </div>
        </div>
        <div style={{ fontSize: 12.5, color: T.gray500, marginBottom: 10 }}>📞 {member.phone}</div>
        {/* Today status */}
        {attSc ? (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, background: attSc.bg, color: attSc.color, border: `1px solid ${attSc.border}`, borderRadius: 20, padding: '3px 10px', fontSize: 11.5, fontWeight: 700 }}>
            <span style={{ width: 6, height: 6, borderRadius: '50%', background: attSc.dot }} />{todayAtt}
          </span>
        ) : (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, background: T.gray100, color: T.gray400, borderRadius: 20, padding: '3px 10px', fontSize: 11.5, fontWeight: 600 }}>
            ○ Not marked
          </span>
        )}
      </div>
      {/* Quick actions */}
      <div style={{ borderTop: `1px solid ${T.gray100}`, padding: '10px 12px', display: 'flex', gap: 6 }}>
        {canMark && <button onClick={e => { e.stopPropagation(); onMarkAtt(member) }} style={{
          flex: 1, padding: '6px', borderRadius: 7, border: `1px solid ${T.teal200}`,
          background: T.teal50, color: T.teal700, fontSize: 11.5, fontWeight: 600,
          cursor: 'pointer', fontFamily: T.font,
        }}>✓ Attendance</button>}
        <button onClick={e => { e.stopPropagation(); onSelect(member) }} style={{
          flex: 1, padding: '6px', borderRadius: 7, border: `1px solid ${T.gray200}`,
          background: T.white, color: T.gray600, fontSize: 11.5, fontWeight: 600,
          cursor: 'pointer', fontFamily: T.font,
        }}>👤 Profile</button>
      </div>
    </div>
  )
}

// ─── Quick attendance popup ────────────────────────────────────────────────────
function QuickAttModal({ member, current, onClose, onMarked }) {
  const { userProfile } = useAuth()
  const [status, setStatus] = useState(current || 'Present')
  const [saving, setSaving] = useState(false)
  async function handleSave() {
    setSaving(true)
    const { error } = await supabase.from('attendance').upsert({ staff_id: member.id, date: isoDate(), status, clinic_id: userProfile?.clinic_id }, { onConflict: 'staff_id,date' })
    if (!error) onMarked(member.id, status)
    else alert(friendlyError(error))
    setSaving(false)
  }
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(8,40,40,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 200, fontFamily: T.font }} onClick={e => { if (e.target === e.currentTarget) onClose() }}>
      <div style={{ background: T.white, borderRadius: 14, width: 320, boxShadow: '0 16px 48px rgba(0,0,0,0.25)', overflow: 'hidden' }}>
        <div style={{ padding: '16px 20px', background: T.teal900 }}>
          <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: T.white }}>Mark Attendance</h3>
          <p style={{ margin: '2px 0 0', fontSize: 12, color: T.teal200 }}>{member.full_name} · Today</p>
        </div>
        <div style={{ padding: 20 }}>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 16 }}>
            {Object.keys(ATT_STATUS).map(s => (
              <button key={s} onClick={() => setStatus(s)} style={{
                padding: '7px 14px', borderRadius: 8, cursor: 'pointer', fontFamily: T.font,
                border: `1.5px solid ${status === s ? ATT_STATUS[s].dot : T.gray200}`,
                background: status === s ? ATT_STATUS[s].bg : T.white,
                color: status === s ? ATT_STATUS[s].color : T.gray500, fontSize: 13, fontWeight: 600,
              }}>{s}</button>
            ))}
          </div>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <Btn variant="secondary" onClick={onClose}>Cancel</Btn>
            <Btn onClick={handleSave} disabled={saving}>{saving ? 'Saving…' : 'Save'}</Btn>
          </div>
        </div>
      </div>
    </div>
  )
}

// ─── Shared nav button style ───────────────────────────────────────────────────
const navBtnSm = {
  padding: '5px 12px', borderRadius: 7, border: `1.5px solid ${T.gray200}`,
  background: T.white, color: T.gray700, fontSize: 14, fontWeight: 700,
  cursor: 'pointer', fontFamily: T.font,
}

// ═══════════════════════════════════════════════════════════════════════════════
// MAIN STAFF MODULE
// ═══════════════════════════════════════════════════════════════════════════════
export default function Staff() {
  const { userProfile, can } = useAuth()
  const isAdmin = userProfile?.role === 'admin'
  const canMark = can('attendance.mark')
  const [staff, setStaff]             = useState([])
  const [loading, setLoading]         = useState(true)
  const [todayAtt, setTodayAtt]       = useState({}) // staffId → status
  const [pendingSalaries, setPending] = useState(0)
  const [selected, setSelected]       = useState(null)
  const [modal, setModal]             = useState(null) // null | 'add' | member (edit)
  const [quickAtt, setQuickAtt]       = useState(null) // member for quick attendance

  const load = useCallback(async () => {
    if (!userProfile) return
    setLoading(true)
    const { data } = await supabase.from('staff').select(isAdmin ? '*' : STAFF_PUBLIC_COLS).order('full_name')
    setStaff(data || [])
    // Load today's attendance
    const { data: att } = await supabase.from('attendance').select('staff_id,status').eq('date', isoDate())
    const attMap = {}
    ;(att || []).forEach(a => { attMap[a.staff_id] = a.status })
    setTodayAtt(attMap)
    // Count pending salaries this month (admin only — salary_payments is admin-only)
    if (isAdmin) {
      const now = new Date()
      const { count } = await supabase.from('salary_payments')
        .select('id', { count: 'exact', head: true })
        .eq('payment_status', 'Pending')
        .eq('month', now.getMonth() + 1)
        .eq('year', now.getFullYear())
      setPending(count || 0)
    }
    setLoading(false)
  }, [isAdmin, userProfile])

  useEffect(() => { load() }, [load])

  // Summary stats
  const presentToday = Object.values(todayAtt).filter(s => s === 'Present').length
  const onLeave      = Object.values(todayAtt).filter(s => s === 'Leave').length

  function handleSaved(saved) {
    setStaff(prev => {
      const idx = prev.findIndex(s => s.id === saved.id)
      if (idx >= 0) { const n = [...prev]; n[idx] = saved; return n }
      return [...prev, saved].sort((a, b) => a.full_name.localeCompare(b.full_name))
    })
    if (selected?.id === saved.id) setSelected(saved)
    setModal(null)
  }
  function handleDeleted(id) { setStaff(prev => prev.filter(s => s.id !== id)); setSelected(null) }
  function handleMarked(id, status) { setTodayAtt(a => ({ ...a, [id]: status })); setQuickAtt(null) }

  const summaryCards = [
    { label: 'Total Staff',      value: staff.length,    icon: '👥', color: T.teal600,   bg: T.teal50  },
    { label: 'Present Today',    value: presentToday,    icon: '✅', color: T.green600,  bg: T.green50 },
    { label: 'On Leave',         value: onLeave,         icon: '🏖️', color: T.amber600,  bg: T.amber50 },
    isAdmin && { label: 'Pending Salaries', value: pendingSalaries, icon: '💰', color: T.purple600, bg: T.purple50 },
  ].filter(Boolean)

  return (
    <div style={{ display: 'flex', height: '100%', fontFamily: T.font, background: T.gray50, overflow: 'hidden' }}>
      {/* ── Main area ── */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {/* Top bar */}
        <div style={{ background: T.white, borderBottom: `1px solid ${T.gray200}`, padding: '18px 24px', flexShrink: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
            <div>
              <h2 style={{ margin: 0, fontSize: 18, fontWeight: 800, color: T.gray900 }}>Staff</h2>
              <p style={{ margin: '2px 0 0', fontSize: 12, color: T.gray400 }}>
                {loading ? 'Loading…' : `${staff.length} team member${staff.length !== 1 ? 's' : ''}`}
              </p>
            </div>
            {isAdmin && <button onClick={() => setModal('add')} style={{
              display: 'flex', alignItems: 'center', gap: 6, padding: '9px 18px', borderRadius: 8,
              border: 'none', background: T.teal600, color: T.white, fontSize: 13, fontWeight: 700,
              cursor: 'pointer', fontFamily: T.font, boxShadow: '0 2px 8px rgba(20,128,128,0.3)',
            }}>+ Add Staff Member</button>}
          </div>
          {/* Summary cards */}
          <div style={{ display: 'flex', gap: 14 }}>
            {summaryCards.map(c => (
              <div key={c.label} style={{
                flex: 1, background: c.bg, borderRadius: 10, padding: '12px 16px',
                border: `1px solid ${c.color}22`, display: 'flex', alignItems: 'center', gap: 12,
              }}>
                <span style={{ fontSize: 24 }}>{c.icon}</span>
                <div>
                  <div style={{ fontSize: 22, fontWeight: 800, color: c.color, lineHeight: 1 }}>{loading ? '—' : c.value}</div>
                  <div style={{ fontSize: 11.5, color: c.color + 'aa', fontWeight: 600, marginTop: 2 }}>{c.label}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Staff grid */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '20px 24px' }}>
          {loading ? (
            <div style={{ textAlign: 'center', padding: '60px 0', color: T.gray400, fontSize: 14 }}>Loading staff…</div>
          ) : staff.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '80px 20px' }}>
              <div style={{ fontSize: 48, marginBottom: 12 }}>👥</div>
              <div style={{ fontSize: 16, fontWeight: 700, color: T.gray600, marginBottom: 6 }}>No staff members yet</div>
              {isAdmin && <button onClick={() => setModal('add')} style={{ padding: '10px 22px', borderRadius: 8, border: 'none', background: T.teal600, color: T.white, fontSize: 13, fontWeight: 700, cursor: 'pointer', fontFamily: T.font }}>+ Add First Member</button>}
            </div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 16 }}>
              {staff.map(m => (
                <StaffCard
                  key={m.id} member={m}
                  todayAtt={todayAtt[m.id]}
                  isSelected={selected?.id === m.id}
                  canMark={canMark}
                  onSelect={m => setSelected(s => s?.id === m.id ? null : m)}
                  onMarkAtt={m => setQuickAtt(m)}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* ── Profile panel ── */}
      {selected && (
        <ProfilePanel
          member={selected}
          todayAtt={todayAtt[selected.id]}
          onClose={() => setSelected(null)}
          onEdit={m => setModal(m)}
          onDeleted={handleDeleted}
        />
      )}

      {/* ── Modals ── */}
      {modal && isAdmin && (
        <StaffModal
          member={modal === 'add' ? null : modal}
          onClose={() => setModal(null)}
          onSaved={handleSaved}
        />
      )}
      {quickAtt && (
        <QuickAttModal
          member={quickAtt}
          current={todayAtt[quickAtt.id]}
          onClose={() => setQuickAtt(null)}
          onMarked={handleMarked}
        />
      )}
    </div>
  )
}
